import { APP_STORE_URL, PLAY_STORE_URL } from '../pages/home-redesign/constants';
import type { AppStorePlatform } from '../pages/home-redesign/inferAppStorePlatform';
import { inferAppStorePlatform } from '../pages/home-redesign/inferAppStorePlatform';

export type InAppBrowser =
  | 'instagram'
  | 'threads'
  | 'facebook'
  | 'messenger'
  | 'linkedin'
  | 'tiktok'
  | 'twitter'
  | 'snapchat'
  | 'pinterest'
  | 'reddit';

const IN_APP_PATTERNS: ReadonlyArray<[RegExp, InAppBrowser]> = [
  [/Instagram/i, 'instagram'],
  [/Threads/i, 'threads'],
  [/FBAN|FBAV|FB_IAB|FBIOS/i, 'facebook'],
  [/Messenger/i, 'messenger'],
  [/LinkedInApp/i, 'linkedin'],
  [/TikTok|BytedanceWebview|musical_ly|Bytedance/i, 'tiktok'],
  [/Twitter/i, 'twitter'],
  [/Snapchat/i, 'snapchat'],
  [/Pinterest/i, 'pinterest'],
  [/Reddit/i, 'reddit'],
];

/** Apps whose WebView blocks JS-initiated redirects without a user tap. */
const NEEDS_USER_GESTURE = new Set<InAppBrowser>([
  'twitter',
  'tiktok',
  'snapchat',
  'linkedin',
  'pinterest',
  'reddit',
]);

export function detectInAppBrowser(userAgent = navigator.userAgent): InAppBrowser | null {
  for (const [pattern, app] of IN_APP_PATTERNS) {
    if (pattern.test(userAgent)) return app;
  }
  return null;
}

export function getStoreUrlForPlatform(platform: AppStorePlatform): string | null {
  if (platform === 'ios') return APP_STORE_URL;
  if (platform === 'android') return PLAY_STORE_URL;
  return null;
}

export function buildAndroidBrowserIntent(url: string): string {
  const parsed = new URL(url);
  return (
    `intent://${parsed.host}${parsed.pathname}${parsed.search}` +
    `#Intent;scheme=https;S.browser_fallback_url=${encodeURIComponent(url)};end`
  );
}

/**
 * Best href for opening `url` from an in-app WebView on iOS.
 * TikTok blocks private Safari schemes (`x-safari-https://`) even on user tap.
 */
export function buildIosExternalBrowserUrl(app: InAppBrowser, url: string): string {
  if (app === 'instagram' || app === 'threads') {
    return `instagram://extbrowser/?url=${encodeURIComponent(url)}`;
  }
  if (app === 'tiktok') {
    return url;
  }
  return `x-safari-${url}`;
}

export function inAppBrowserNeedsUserGesture(app: InAppBrowser): boolean {
  return NEEDS_USER_GESTURE.has(app);
}

export function openUrlInExternalBrowser(app: InAppBrowser, url: string): void {
  if (/Android/i.test(navigator.userAgent)) {
    window.location.href = buildAndroidBrowserIntent(url);
    return;
  }
  // TikTok iOS blocks custom schemes; a direct https navigation is more reliable.
  window.location.href = buildIosExternalBrowserUrl(app, url);
}

/** Prefer a native `<a href>` on iOS in-app browsers — gesture handling is stricter for JS redirects. */
export function shouldUseNativeStoreLink(app: InAppBrowser | null): boolean {
  return app !== null && /iPhone|iPad|iPod/i.test(navigator.userAgent);
}

export type SmartAppLinkMode = 'landing' | 'escape-prompt' | 'redirecting';

export function resolveSmartAppLinkMode(
  userAgent: string,
): { mode: SmartAppLinkMode; inAppBrowser: InAppBrowser | null; storeUrl: string | null } {
  const storeUrl = getStoreUrlForPlatform(inferAppStorePlatform());
  const inAppBrowser = detectInAppBrowser(userAgent);

  return { mode: 'landing', inAppBrowser, storeUrl };
}

export function triggerSmartAppLinkRedirect(
  inAppBrowser: InAppBrowser | null,
  storeUrl: string,
): void {
  if (inAppBrowser) {
    openUrlInExternalBrowser(inAppBrowser, storeUrl);
    return;
  }
  window.location.replace(storeUrl);
}
